/**
 * Plan tier catalogue — the single source of truth for pricing display across
 * the app (checkout picker, marketing pricing page, homepage teaser).
 *
 * Mirrors the business model in CLAUDE.md. Price strings are DISPLAY-ONLY; the
 * real charge amounts live on the Stripe Prices keyed by `plan_tier` (see
 * config.py `stripe_price_for_tier`). `features` drives the pricing comparison.
 */
import { GRANTMAKERS, fmt } from './corpus'

// Per-tier focus-area caps — mirrors backend/constants.py
// TIER_FOCUS_AREA_LIMITS (the backend enforces these at save time).
// null = unlimited (every category).
export const FOCUS_AREA_LIMITS = {
  starter: 3,
  growth: 10,
  pro: null,
  consultant: null,
}

// Tiers that can opt into the daily digest — mirrors backend/constants.py
// DAILY_DIGEST_TIERS (the backend gates both the preference write and the
// daily send itself).
export const DAILY_DIGEST_TIERS = ['pro', 'consultant']

// Tiers that may search the whole funder corpus — mirrors backend/constants.py
// CORPUS_SEARCH_TIERS (ADR-0012). DISPLAY ONLY: the server's require_tier gate
// is authoritative and 403s regardless of what this array says. Used to label
// the feature as Pro up front, so a Growth client meets the upgrade prompt
// before the request rather than after it.
export const CORPUS_SEARCH_TIERS = ['pro', 'consultant']

// Foundation leads shown per digest, by tier — mirrors backend/digest/builder.py
// FOUNDATION_LEAD_LIMITS. Foundations ship in EVERY paid tier (owner decision
// 2026-07-16); tiers differentiate on VOLUME + features, not access. Display /
// marketing only — the backend cap is authoritative.
export const FOUNDATION_LEAD_LIMITS = {
  starter: 3,
  growth: 5,
  pro: 8,
  consultant: 8,
}

// Co-funder leads per tier — mirrors backend/constants.py COFUNDER_LEAD_LIMITS.
//
// ⚠️ GRADED, NOT GATED ([ADR-0014](docs/adr/0014-cofunder-leads-are-graded-not-gated.md)),
// which is the OPPOSITE of CORPUS_SEARCH_TIERS above and the reason both live
// here side by side. Every paying tier sees co-funder leads; only the depth
// changes. The pricing page listed this as a Growth feature until 2026-08-13,
// which was wrong in both directions: it undersold Starter and it implied a
// gate that does not exist.
//
// The rationale is worth not re-deriving: a feature that no-shows cannot anchor
// a price tier. Only 12,333 of the 20,483 funders in the graph have peers at
// all, and a client whose EIN is absent from every filing gets "we don't know".
export const COFUNDER_LEAD_LIMITS = {
  starter: 3,
  growth: 5,
  pro: 8,
  consultant: 8,
}

// Max selectable focus areas for a tier as a number (Infinity = unlimited).
// Unknown tiers get Starter's limit, matching the backend's conservatism.
export function focusAreaLimit(tierKey) {
  const limit = tierKey in FOCUS_AREA_LIMITS
    ? FOCUS_AREA_LIMITS[tierKey]
    : FOCUS_AREA_LIMITS.starter
  return limit === null ? Infinity : limit
}

export const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '$39',
    blurb: 'Foundation leads + federal grants, weekly, up to 3 focus areas.',
    tagline: 'For a single small nonprofit getting started.',
    features: [
      `${FOUNDATION_LEAD_LIMITS.starter} foundation leads per digest`,
      `${COFUNDER_LEAD_LIMITS.starter} co-funder leads`,
      'Federal grants included',
      'Weekly Tuesday digest',
      'Search your matched foundations',
      `Up to ${FOCUS_AREA_LIMITS.starter} focus areas`,
      'Relevance feedback loop',
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: '$79',
    blurb: 'More foundation leads, deadline alerts, up to 10 focus areas.',
    tagline: 'For organizations actively chasing more funding.',
    highlight: true,
    features: [
      'Everything in Starter',
      `${FOUNDATION_LEAD_LIMITS.growth} foundation leads per digest`,
      `${COFUNDER_LEAD_LIMITS.growth} co-funder leads`,
      'Deadline alerts',
      `Up to ${FOCUS_AREA_LIMITS.growth} focus areas`,
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$149',
    blurb: 'Search every grantmaker on record, daily digest, unlimited focus areas.',
    tagline: 'For development teams that want everything.',
    features: [
      'Everything in Growth',
      `${FOUNDATION_LEAD_LIMITS.pro} foundation leads per digest`,
      `${COFUNDER_LEAD_LIMITS.pro} co-funder leads`,
      // The real Pro differentiator (ADR-0012). Replaced "IRS 990 insights",
      // which described nothing a buyer could picture and was the vaguest line
      // on the pricing page.
      `Search all ${fmt(GRANTMAKERS)} grantmakers`,
      'Why each one is or is not in your digest',
      'Daily digest option',
      'Unlimited focus areas',
    ],
  },
  {
    key: 'consultant',
    name: 'Consultant',
    price: '$249',
    blurb: 'Pro + multi-client dashboard and white-label digests (coming soon).',
    tagline: 'For freelance grant writers serving many clients.',
    features: [
      'Everything in Pro',
      'Multi-client dashboard (coming soon)',
      'White-label digests (coming soon)',
      'Up to 15 clients',
    ],
  },
]

export function tierByKey(key) {
  return TIERS.find(t => t.key === key)
}
